import { useState, useEffect, Fragment } from "react";
import { db, functions } from "../firebaseConfig";
import { collection, onSnapshot, query, where, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { partnerId } from "../partnerConfig";

import { useAuth } from "../AuthContext";
import Header from "../components/Header";
import SideScreen from "../components/SideScreen";
import { ListButton, BlockButton } from "../components/Buttons";

import style from "../style/screens/Point.module.css";

interface PointData {
    id: string;
    inputAmt: number;
    inputType: string;
    kiosk: string;
    owner: string;
    pointAmt: number;
    pointDelta: number;
    timestamp: number;
    type: "DEPOSIT" | "WITHDRAW" | "DONATE";
}

interface WithdrawMethod {
    id: string;
    type: "COUPON";
    amount: number;
    title: string;
    active: boolean;
    image: string;
}

const usePoint = () => {
    const { user } = useAuth();
    const [points, setPoints] = useState<PointData[]>([]);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            query(collection(db, "receipt"), where("owner", "==", user!.uid), where("partnerId", "==", partnerId)),
            (snapshot) => {
                const pointLists = snapshot.docs
                    .map((doc) => doc.data() as PointData)
                    .sort((a, b) => b.timestamp - a.timestamp);
                setPoints(pointLists);
            }
        );
        return () => unsubscribe();
    }, [user]);

    return points;
};

const useWithdrawMethod = () => {
    const [method, setMethods] = useState<WithdrawMethod[]>([]);

    useEffect(() => {
        const unsubscribe = onSnapshot(
            query(collection(db, `partner/${partnerId}/withdraw`), where("active", "==", true)),
            (snapshot) => {
                const methodLists = snapshot.docs
                    .map((doc) => doc.data() as WithdrawMethod)
                    .sort((a, b) => a.amount - b.amount);
                setMethods(methodLists);
            }
        );
        return () => unsubscribe();
    }, []);

    return method;
};

const Point = ({}) => {
    const points = usePoint();
    const [pointWithdraw, setPointWithdraw] = useState<boolean>(false);
    const [pointInform, setPointInform] = useState<boolean>(false);

    const groupByDate = (data: PointData[]) => {
        const grouped: { [key: string]: PointData[] } = {};
        data.forEach((d) => {
            const date = new Date(d.timestamp);
            const formattedDate = `${String(date.getFullYear()).slice(2)}년 ${String(date.getMonth() + 1).padStart(
                2,
                "0"
            )}월 ${String(date.getDate()).padStart(2, "0")}일`;
            if (!grouped[formattedDate]) {
                grouped[formattedDate] = [];
            }
            grouped[formattedDate].push(d);
        });
        return Object.entries(grouped).map(([date, receipts]) => ({
            date,
            receipts,
        }));
    };

    return (
        <>
            <Header></Header>
            <div className={style["container"]}>
                <div className={style["unit--totalPoint"]}>
                    <p className={[style["text--totalPoint"], style["text--totalPoint--eyebrow"]].join(" ")}>
                        보유 포인트
                    </p>

                    <div className={style["wrapper--totalPoint"]}>
                        <h4 className={style["text--totalPoint"]}>{points[0]?.pointAmt.toLocaleString() || 0}</h4>

                        <ListButton text="환전하기" onClick={() => setPointWithdraw(true)} />
                    </div>
                </div>
                <div className={style["unit--pointHistory"]}>
                    <div className={style["wrapper--pointHistory"]}>
                        {points.length == 0 ? (
                            <p className={style["text--alert"]}>포인트 적립 내역이 없습니다</p>
                        ) : (
                            groupByDate(points).map((datas, index) => (
                                <Fragment key={index}>
                                    <p className={style["text--pointHistory--date"]}>{datas.date}</p>
                                    {datas.receipts.map((receipt, idx) => (
                                        <HistoryList
                                            key={idx}
                                            type={receipt.type}
                                            amount={receipt.pointDelta}
                                            account={receipt.pointAmt}
                                            timestamp={receipt.timestamp}
                                        />
                                    ))}
                                </Fragment>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <SideScreen
                appear={pointWithdraw}
                title="포인트 환전하기"
                id="P1"
                close={() => setPointWithdraw(false)}
                // inform={() => setPointInform(true)}
            >
                <Request close={() => setPointWithdraw(false)} />
            </SideScreen>

            {/* <SideScreen appear={pointInform} title="포인트 환전 안내" id="P2" close={() => setPointInform(false)}>
                <div className={style["container--notice"]}>
                    <h4 className={style["text--notice--title"]}>
                        포인트는 곧<br />
                        환전하실 수 있어요!
                    </h4>
                    <p className={style["text--notice--copy"]}>
                        준비 중입니다. 잠시만 기다려주세요!
                        <br />
                        <br />
                        환전이 활성화되기 전에도 적립은 가능합니다. 포인트를 환전하려면 최소 1,000포인트가 있어야
                        합니다.
                        <br />
                        <br />
                        개인별 환전 한도는 국가나 지역에 따라 달라집니다.
                        <br />
                        <br />
                        문의메일
                        <br />
                        <a href="mailto:leo@hi-vits.com">leo@hi-vits.com</a>
                    </p>
                </div>
            </SideScreen> */}
        </>
    );
};

const HistoryList = ({
    type = "DEPOSIT",
    amount,
    account,
    timestamp,
}: {
    type?: "DEPOSIT" | "WITHDRAW" | "DONATE";
    amount: number;
    account: number;
    timestamp: number;
}) => {
    const date = new Date(timestamp);
    const hours = String(date.getHours()).padStart(2, "0"); // 두 자리 수로 포맷
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const HHMM = `${hours}:${minutes}`;

    return (
        <div className={style["unit--historyList"]}>
            <span
                className={[
                    style["icon--historyList"],
                    // color by type
                    type === "DEPOSIT" && style["icon--historyList--deposit"],
                    type === "WITHDRAW" && style["icon--historyList--withdraw"],
                    type === "DONATE" && style["icon--historyList--donate"],
                ].join(" ")}
            >
                {type == "DEPOSIT" && (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="20"
                        viewBox="0 0 16 20"
                        fill="var(--color-brand)"
                    >
                        <path d="M6.15762 0V9.2381C6.91997 8.44048 7.61759 7.87857 8.25048 7.55C8.88337 7.22143 9.54263 7.05476 10.2259 7.05476C11.0961 7.05476 11.8417 7.3 12.4602 7.79286C13.0787 8.28571 13.4886 8.8619 13.6924 9.52619C13.8962 10.1905 13.9968 11.3095 13.9968 12.881V17.0048C13.9968 18.0571 14.0951 18.7143 14.2941 18.9738C14.4931 19.2333 14.8934 19.4 15.4976 19.469V20H8.45905V19.469C8.95289 19.4 9.32448 19.2048 9.5738 18.8786C9.7512 18.6238 9.8399 17.9976 9.8399 17.0048V12.1143C9.8399 10.9738 9.79435 10.2595 9.70565 9.96905C9.59537 9.60714 9.38681 9.34524 9.08474 9.18333C8.90734 9.0881 8.71076 9.04524 8.50939 9.04762C8.12102 9.05238 7.74465 9.1881 7.37546 9.45238C7.30114 9.50714 7.22443 9.56905 7.14532 9.63809C6.50044 10.2238 6.15762 11.0714 6.15762 11.9405V17.0048C6.15762 17.9881 6.23194 18.6024 6.38057 18.8476C6.56756 19.181 6.95353 19.3881 7.53848 19.4667V19.9976H0.49996V19.4667C1.0849 19.4071 1.49964 19.2214 1.74656 18.9071C1.91438 18.6905 1.99828 18.0571 1.99828 17.0048V2.99524C1.99828 1.95238 1.90239 1.30476 1.70821 1.04762C1.51402 0.792857 1.11128 0.62381 0.497562 0.545238V0H6.15523H6.15762Z" />
                        <path d="M11.7961 0.785706C12.4003 0.785706 12.9109 0.995229 13.3304 1.41666C13.7476 1.83571 13.9585 2.34285 13.9585 2.93332C13.9585 3.5238 13.7476 4.02618 13.3232 4.44285C12.9013 4.85713 12.3907 5.06666 11.7961 5.06666C11.2016 5.06666 10.6958 4.85952 10.2763 4.44285C9.85912 4.02856 9.64816 3.5238 9.64816 2.93332C9.64816 2.34285 9.85672 1.83809 10.2763 1.41666C10.6934 0.99761 11.2016 0.785706 11.7961 0.785706Z" />
                    </svg>
                )}

                {type == "WITHDRAW" && (
                    <svg width="24" height="17" viewBox="0 0 24 17" fill="#3182F6" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23.785 0.805C23.215 0.855 22.785 1.145 22.495 1.695C22.375 1.905 22.135 2.515 21.755 3.515L21.425 4.385H23.075V5.905H20.845L19.965 8.215H23.075V9.735H19.385L16.775 16.625H16.315L13.665 9.735H10.955L8.035 16.625H7.605L4.825 9.735H0.905002V8.215H4.225L3.285 5.905H0.905002V4.385H2.675L2.385 3.655C1.925 2.505 1.685 1.905 1.655 1.865C1.295 1.215 0.805002 0.865 0.205002 0.805V0.375H7.095V0.805C6.175 0.825 5.715 1.105 5.715 1.625C5.715 1.935 5.905 2.565 6.285 3.515L6.635 4.385H11.585L11.245 3.515C10.965 2.785 10.775 2.325 10.675 2.135C10.385 1.555 10.075 1.185 9.745 1.015C9.495 0.885 9.125 0.815 8.625 0.815V0.385H16.325V0.815C15.945 0.815 15.665 0.835 15.495 0.865C15.145 0.925 14.905 1.045 14.785 1.215C14.695 1.345 14.645 1.495 14.645 1.665C14.645 1.855 14.825 2.405 15.175 3.315L15.585 4.395H20.515L20.655 4.035C21.055 2.985 21.255 2.265 21.255 1.875C21.255 1.195 20.725 0.845 19.675 0.825V0.395H23.795V0.825L23.785 0.805ZM11.605 5.905H7.245L8.185 8.215H10.655L11.605 5.905ZM10.025 9.735H8.805L9.405 11.225L10.025 9.735ZM13.085 8.215L12.385 6.385L11.615 8.215H13.095H13.085ZM19.925 5.905H16.155L17.045 8.215H19.045L19.925 5.905ZM18.465 9.735H17.635L18.055 10.825L18.465 9.735Z" />
                    </svg>
                )}

                {type == "DONATE" && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="#848484">
                        <path d="M6.15762 0V9.2381C6.91997 8.44048 7.61759 7.87857 8.25048 7.55C8.88337 7.22143 9.54263 7.05476 10.2259 7.05476C11.0961 7.05476 11.8417 7.3 12.4602 7.79286C13.0787 8.28571 13.4886 8.8619 13.6924 9.52619C13.8962 10.1905 13.9968 11.3095 13.9968 12.881V17.0048C13.9968 18.0571 14.0951 18.7143 14.2941 18.9738C14.4931 19.2333 14.8934 19.4 15.4976 19.469V20H8.45905V19.469C8.95289 19.4 9.32448 19.2048 9.5738 18.8786C9.7512 18.6238 9.8399 17.9976 9.8399 17.0048V12.1143C9.8399 10.9738 9.79435 10.2595 9.70565 9.96905C9.59537 9.60714 9.38681 9.34524 9.08474 9.18333C8.90734 9.0881 8.71076 9.04524 8.50939 9.04762C8.12102 9.05238 7.74465 9.1881 7.37546 9.45238C7.30114 9.50714 7.22443 9.56905 7.14532 9.63809C6.50044 10.2238 6.15762 11.0714 6.15762 11.9405V17.0048C6.15762 17.9881 6.23194 18.6024 6.38057 18.8476C6.56756 19.181 6.95353 19.3881 7.53848 19.4667V19.9976H0.49996V19.4667C1.0849 19.4071 1.49964 19.2214 1.74656 18.9071C1.91438 18.6905 1.99828 18.0571 1.99828 17.0048V2.99524C1.99828 1.95238 1.90239 1.30476 1.70821 1.04762C1.51402 0.792857 1.11128 0.62381 0.497562 0.545238V0H6.15523H6.15762Z" />
                        <path d="M11.7961 0.785706C12.4003 0.785706 12.9109 0.995229 13.3304 1.41666C13.7476 1.83571 13.9585 2.34285 13.9585 2.93332C13.9585 3.5238 13.7476 4.02618 13.3232 4.44285C12.9013 4.85713 12.3907 5.06666 11.7961 5.06666C11.2016 5.06666 10.6958 4.85952 10.2763 4.44285C9.85912 4.02856 9.64816 3.5238 9.64816 2.93332C9.64816 2.34285 9.85672 1.83809 10.2763 1.41666C10.6934 0.99761 11.2016 0.785706 11.7961 0.785706Z" />
                    </svg>
                )}
            </span>

            <div className={style["wrapper--historyList"]}>
                <h4 className={style["text--historyList"]}>
                    {type == "DEPOSIT" && "페트병 포인트 적립"}
                    {type == "WITHDRAW" && "포인트 환전"}
                    {type == "DONATE" && "페트병 기부"}
                    <span>{HHMM}</span>
                </h4>
                <h4
                    className={[
                        style["text--historyList"],
                        style["text--historyList--data"],
                        // color by type
                        type === "DEPOSIT" && style["text--historyList--deposit"],
                        type === "WITHDRAW" && style["text--historyList--withdraw"],
                        type === "DONATE" && style["text--historyList--donate"],
                    ].join(" ")}
                >
                    {type == "DEPOSIT" && `+${amount.toLocaleString()}pt`}
                    {type == "WITHDRAW" && `-${amount.toLocaleString()}pt`}
                    {type == "DONATE" && `${amount.toLocaleString()}pt`}
                    <span>{account.toLocaleString()}pt</span>
                </h4>
            </div>
        </div>
    );
};

const WithdrawSelector = ({
    options,
    selected,
    onChange,
}: {
    options: WithdrawMethod[];
    selected: string;
    onChange: (value: WithdrawMethod) => void;
}) => {
    return (
        <div className={style["container--withdraw-option"]}>
            {options.map((option) => (
                <label
                    key={option.id}
                    className={[
                        style["unit--withdraw-option"],
                        selected && selected == option.id && style["unit--withdraw-option--selected"],
                        selected && selected != option.id && style["unit--withdraw-option--not-selected"],
                    ].join(" ")}
                >
                    <input
                        type="radio"
                        name="withdraw-option"
                        value={option.amount}
                        checked={selected === option.id}
                        onChange={() => onChange(option)}
                        className={style["input--withdraw-option"]}
                    />
                    <img src={option.image} />
                    <p className={style["text--withdraw-option"]}>{option.title}</p>
                </label>
            ))}
        </div>
    );
};

const Request = ({ close }: { close: () => void }) => {
    const points = usePoint();
    const methods = useWithdrawMethod();

    const [selected, setSelected] = useState<string>("");
    const [amount, setAmount] = useState<number>(0);
    const [needs, setNeeds] = useState<number>(0);
    const [complete, setComplete] = useState<boolean>(false);
    const [available, setAvailable] = useState<boolean>(false);
    const [acitve, setActive] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    var onChange = (option: WithdrawMethod) => {
        setSelected(option.id);
        setAmount(option.amount);

        if (points.length == 0) {
            setAvailable(false);
            setActive(false);
            setNeeds(option.amount);
            return;
        }

        setAvailable(points[0].pointAmt >= option.amount);
        setActive(points[0].pointAmt >= option.amount);
        if (points[0].pointAmt < option.amount) {
            setNeeds(option.amount - points[0].pointAmt);
        }
    };

    var request = async () => {
        setLoading(true);

        return httpsCallable(
            functions,
            "requestWithdraw"
        )({
            option: selected,
            amount: amount,
            partner: partnerId,
        })
            .then(() => {
                setLoading(false);
                setComplete(true);
            })
            .catch((err) => {
                setLoading(false);
                console.error(err);
            });
    };

    return (
        <>
            {complete ? (
                <div className={style["container--withdraw-complete"]}>
                    <div className={style["wrapper--withdraw-complete"]}>
                        <span className={style["icon--withdraw-complete"]}>
                            <svg
                                width="74"
                                height="74"
                                viewBox="0 0 74 74"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M37 74C16.7951 74 0 57.2049 0 37C0 16.7588 16.7588 0 36.9637 0C57.2049 0 74 16.7588 74 37C74 57.2049 57.2412 74 37 74ZM32.9373 54.7382C34.1706 54.7382 35.2225 54.1578 35.9843 52.9608L53.0333 26.1176C53.4686 25.3559 53.9402 24.5216 53.9402 23.7235C53.9402 22.0186 52.4529 20.9304 50.8569 20.9304C49.9137 20.9304 48.9706 21.5108 48.2814 22.599L32.7922 47.4833L25.4284 37.9794C24.5216 36.7824 23.7235 36.4559 22.6716 36.4559C21.0392 36.4559 19.7696 37.798 19.7696 39.4304C19.7696 40.2647 20.0961 41.0627 20.6402 41.7882L29.7451 52.9608C30.6882 54.2304 31.7039 54.7382 32.9373 54.7382Z"
                                    fill="#00A343"
                                />
                            </svg>
                        </span>

                        <h2 className={style["text--withdraw-complete--title"]}>환전 신청 완료!</h2>

                        <p className={style["text--withdraw-complete--copy"]}>
                            관리자의 승인을 기다려주세요
                            <br />
                            매주 월요일에 쿠폰이 일괄 발송됩니다
                        </p>
                    </div>
                    <div className={style["wrapper--footer"]}>
                        <BlockButton type="Primary" text="완료" onClick={close} />
                    </div>
                </div>
            ) : (
                <>
                    <div className={style["wrapper--withdraw"]}>
                        <h4 className={style["text--withdraw--title"]}>환전 가능 포인트</h4>
                        <p className={style["text--withdraw--point"]}>{points[0]?.pointAmt.toLocaleString() || 0}pt</p>

                        <WithdrawSelector options={methods} selected={selected} onChange={onChange} />

                        <div className={style["wrapper--floating-message"]}>
                            <span
                                className={[
                                    style["unit--floating-message"],
                                    selected && !available && style["unit--floating-message--active"],
                                ].join(" ")}
                            >
                                {needs}pt가 더 필요해요
                            </span>
                        </div>
                    </div>

                    <div className={style["container--notice"]}>
                        <p className={style["text--notice--copy"]}>
                            포인트를 환전 하려면 최소 1,000포인트가 있어야 합니다.
                            <br />
                            환전은 다이소 상품권으로만 가능합니다.
                            <br />
                            매주 월요일에 일괄로 문자를 통해 상품권을 보내 드립니다.
                            <br />
                            <br />
                            개인별 환전 한도는 국가나 지역에 따라 달라집니다.
                            <br />
                            <br />
                            문의메일: <a href="mailto:leo@hi-vits.com">leo@hi-vits.com</a>
                        </p>
                    </div>

                    <div className={style["wrapper--footer"]}>
                        <BlockButton
                            type="Primary"
                            text="환전 신청하기"
                            disabled={!acitve}
                            loading={loading}
                            onClick={request}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default Point;
